import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '@/contexts/I18nContext';
import { Notification } from '@/hooks/useNotifications';
import { cn } from '@/lib/utils';
import { Star, MessageCircle, Handshake, Target, Clock, Users, Dumbbell, Bell } from 'lucide-react';

interface NotificationItemProps {
  notification: Notification;
  onClick: (notification: Notification) => void;
}

export const NotificationItem: React.FC<NotificationItemProps> = ({ 
  notification, 
  onClick 
}) => {
  const { t, currentLanguage } = useI18n();
  const navigate = useNavigate();
  const dateLocale = currentLanguage === 'pt' ? ptBR : enUS;

  // Function to get translated content for training goal notifications
  const getTranslatedContent = (notification: Notification): string => {
    if (notification.type === 'goal') {
      const percentage = notification.metadata?.percentage;
      
      if (percentage !== undefined) {
        // Check for specific milestones first
        if (percentage >= 100) {
          return t('notifications.training_goals.100_percent', {});
        } else if (percentage >= 80) {
          return t('notifications.training_goals.80_percent', { percentage });
        } else if (percentage >= 70) {
          return t('notifications.training_goals.70_percent', { percentage });
        } else {
          // For any other percentage, use a generic translation
          return t('notifications.training_goals.generic_percent', { percentage });
        }
      }
    }
    
    // Return original content for other notification types
    return notification.content;
  };

  // Function to get translated title for training goal notifications
  const getTranslatedTitle = (notification: Notification): string => {
    if (notification.type === 'goal') {
      return t('notifications.training_goals.title', {});
    }
    
    // Return original title for other notification types
    return notification.title;
  };

  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'feedback':
        return <Star className="w-6 h-6 text-yellow-500" />;
      case 'interaction':
        return <MessageCircle className="w-6 h-6 text-red-500" />;
      case 'connection':
        return <Handshake className="w-6 h-6 text-green-500" />;
      case 'goal':
        return <Target className="w-6 h-6 text-purple-500" />;
      case 'reminder':
        return <Clock className="w-6 h-6 text-orange-500" />;
      case 'partner':
        return <Users className="w-6 h-6 text-red-500" />;
      case 'training_reminder':
        return <Dumbbell className="w-6 h-6 text-red-500" />;
      default:
        return <Bell className="w-6 h-6 text-gray-500" />;
    }
  };

  const getNotificationColor = (type: Notification['type']) => {
    switch (type) {
      case 'feedback':
        return 'border-l-yellow-500 bg-white hover:bg-gray-50';
      case 'interaction':
        return 'border-l-red-500 bg-white hover:bg-gray-50';
      case 'connection':
        return 'border-l-green-500 bg-white hover:bg-gray-50';
      case 'goal':
        return 'border-l-purple-500 bg-white hover:bg-gray-50';
      case 'reminder':
        return 'border-l-orange-500 bg-white hover:bg-gray-50';
      case 'partner':
        return 'border-l-red-500 bg-white hover:bg-gray-50';
      case 'training_reminder':
        return 'border-l-red-500 bg-white hover:bg-gray-50';
      default:
        return 'border-l-gray-500 bg-white hover:bg-gray-50';
    }
  };

  const handleClick = () => {
    // Chamar a função onClick passada como prop
    onClick(notification);
  };

  return (
    <div
      onClick={handleClick}
      className={cn(
        'relative p-4 border-l-4 cursor-pointer transition-all duration-200',
        'hover:shadow-md hover:scale-[1.02] active:scale-[0.98]',
        'border border-gray-200 rounded-r-lg',
        getNotificationColor(notification.type),
        !notification.is_read && 'bg-red-50', // Highlight unread with red theme
        notification.is_read && 'opacity-80'
      )}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      aria-label={`${getTranslatedTitle(notification)}: ${getTranslatedContent(notification)}`}
    >
      {/* Unread indicator */}
      {!notification.is_read && (
        <div className="absolute top-3 right-3">
          <div className="w-2 h-2 bg-red-500 rounded-full"></div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start gap-3 mb-2">
        {getNotificationIcon(notification.type)}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 text-sm leading-tight">
            {getTranslatedTitle(notification)}
          </h3>
          <p className="text-gray-600 text-sm mt-1 leading-relaxed">
            {getTranslatedContent(notification)}
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between mt-3">
        <span className="text-gray-500 text-xs">
          {formatDistanceToNow(new Date(notification.created_at), { 
            addSuffix: true,
            locale: dateLocale 
          })}
        </span>
        
        {!notification.is_read && (
          <span className="text-red-500 text-xs font-medium">
            New
          </span>
        )}
      </div>
    </div>
  );
};
